import * as React from 'react';
import {useContext, useEffect, useState} from 'react';
import {Modal} from '@flexe/ui-components';
import {GridRowId, GridRowSelectionModel} from '@mui/x-data-grid';
import FlexeContext from '../../../contexts/FlexeContext';
import LoadsService from '../../../shared/services/LoadsService';
import {CreateLoadResultInfo, EligibleForLoadShipment, UpsertLoadRequest, UpsertLoadState} from '../LoadInterfaces';
import {VacantDockLocation} from '../../outbound-manifests/ManifestInterfaces';
import OutboundManifestService from '../../../shared/services/OutboundManifestService';
import SelectShipments from './SelectShipments';
import PreviewShipments from './PreviewShipments';

/**
 * WMS-6029 Load shipment pre-planning has been commented out/disabled until such functionality supports multi stop loads
 */

interface Props {
  isOpen: boolean;
  toggleModal: () => void;
  cancelUpsert: () => void;
  warehouseId: number;
  loadState: UpsertLoadState; // the state of the load at time of modal entry
  onCreateLoad?: () => void;
  onEditLoad?: (loadState: UpsertLoadState) => void;
}

export enum UpsertMode {
  CREATE = 'Create Load',
  EDIT = 'Edit Load'
}

export function UpsertLoadModal(props: Props) {
  const upsertMode = props.loadState != null ? UpsertMode.EDIT : UpsertMode.CREATE;
  const {authenticityToken} = useContext(FlexeContext);
  const loadsService = new LoadsService(authenticityToken);
  const outboundManifestService = new OutboundManifestService(authenticityToken);

  const [creationResultInfo, setCreationResultInfo] = useState<CreateLoadResultInfo>(null);
  const [isLoading, setIsLoading] = useState(true); // WMS-6029
  const [error, setError] = useState<string | null>();
  const [availableShipments, setAvailableShipments] = useState<EligibleForLoadShipment[]>([]);
  const [selectedShipments, setSelectedShipments] = useState<EligibleForLoadShipment[]>([]);
  const [shipmentIdToDelete, setShipmentIdToDelete] = useState<GridRowId | null>(null);
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [dockLocs, setDockLocs] = useState<VacantDockLocation[]>([]);
  const [loadState, setLoadState] = useState<UpsertLoadState>(props.loadState);

  useEffect(() => {
    setLoadState(props.loadState);
  }, [props.loadState]);

  useEffect(() => {
    const updatedShipments = availableShipments.filter((sh) => {
      return rowSelectionModel.includes(sh.id);
    });
    setSelectedShipments(updatedShipments);

    setLoadState((prevState) => ({
      ...prevState,
      shipmentIds: updatedShipments.map((shipment) => shipment.id)
    }));
  }, [rowSelectionModel, availableShipments]);

  useEffect(() => {
    if (props.isOpen) {
      // this will refresh the list of shipments when the create load dialog is opened
      // if the dialog is closed and re-opened, the list of shipments will be refreshed
      // fetchShipments();
      fetchVacantDockLocations();
    }
  }, [props.isOpen]);

  useEffect(() => {
    // we define this function inside the useEffect so the value of rowSelectionModel
    // doesn't hang about and get stale
    const handleShipmentDelete = (shipmentToRemove: GridRowId) => {
      const updatedShipmentIds = [];
      rowSelectionModel.forEach((id) => {
        if (id !== shipmentToRemove) {
          updatedShipmentIds.push(id);
        }
      });
      setRowSelectionModel(updatedShipmentIds);
      setLoadState((prevState) => ({
        ...prevState,
        shipmentIds: updatedShipmentIds // the gridRowId for each shipment is the shipment id so this is safe
      }));
    };
    if (shipmentIdToDelete) {
      handleShipmentDelete(shipmentIdToDelete);
    }
    setShipmentIdToDelete(null);
  }, [shipmentIdToDelete]);

  // WMS-6029
  const fetchShipments = async () => {
    try {
      const response = await loadsService.fetchEligibleShipmentsForNewLoad(
        props.warehouseId,
        props.loadState?.freightLoadId
      );
      const shipments = response.data;
      if (response.errors.length > 0) {
        setError(response.errors[0]?.detail || 'An internal error has occurred');
      }
      setAvailableShipments(shipments);

      // pre-select shipments already on the load being edited
      if (upsertMode === UpsertMode.EDIT) {
        setRowSelectionModel(props.loadState.shipmentIds);
      }
    } catch (e) {
      setError(e.toString());
    } finally {
      setIsLoading(false);
    }
  };

  const fetchVacantDockLocations = async () => {
    try {
      const response = await outboundManifestService.retrieveVacantDockLocations(props.warehouseId);
      if (response.errors?.length > 0) {
        setError(response.errors[0]?.detail || 'An internal error has occurred');
      }

      const loadIsAlreadyAssignedDock = props.loadState?.dockLocationId != null;
      if (loadIsAlreadyAssignedDock) {
        const assignedDock: VacantDockLocation = {
          location_id: props.loadState.dockLocationId,
          location_label: props.loadState.dockLocationLabel,
          location_barcode_text: props.loadState.dockLocationLabel // doesn't have to be correct
        };
        setDockLocs(response.data.vacant_locations.concat([assignedDock]));
      } else {
        setDockLocs(response.data.vacant_locations);
      }
    } catch (e) {
      setError(e.toString());
    }
  };

  useEffect(() => {
    // we define this function inside the useEffect so the value of rowSelectionModel
    // doesn't hang about and get stale
    const handleShipmentDelete = (shipmentToRemove: GridRowId) => {
      const updatedShipmentIds = [];
      rowSelectionModel.forEach((id) => {
        if (id !== shipmentToRemove) {
          updatedShipmentIds.push(id);
        }
      });
      setRowSelectionModel(updatedShipmentIds);
    };
    if (shipmentIdToDelete) {
      handleShipmentDelete(shipmentIdToDelete);
    }
    setShipmentIdToDelete(null);
  }, [shipmentIdToDelete]);

  const handleToggle = () => {
    clearState();
    props.toggleModal();
  };

  // WMS-6029
  const handleProceedToPreview = () => {
    setShowPreview(true);
  };

  const handleGoBack = () => {
    setShowPreview(false);
  };

  const handleDeleteId = (shipmentToRemove: GridRowId) => {
    setShipmentIdToDelete(shipmentToRemove);
  };

  const handleSelectionModelChange = (shipmentsToInclude) => {
    setRowSelectionModel(shipmentsToInclude);
  };

  const editsWereMade = (): boolean => {
    const scacChanged = loadState?.scac !== props.loadState?.scac;
    const trailerNumberChanged = loadState?.trailerNumber !== props.loadState?.trailerNumber;
    const proNumberChanged = loadState?.proNumber !== props.loadState?.proNumber;
    const dockLocationChanged = loadState?.dockLocationLabel !== props.loadState?.dockLocationLabel;
    const sealNumberChanged = loadState?.sealNumber !== props.loadState?.sealNumber;

    const oldSortedShipments = (props.loadState?.shipmentIds || []).sort((n1, n2) => n1 - n2);
    const newSortedShipments = (loadState?.shipmentIds || []).sort((n1, n2) => n1 - n2);
    const shipmentsWereChanged = oldSortedShipments !== newSortedShipments;

    return [
      scacChanged,
      trailerNumberChanged,
      proNumberChanged,
      dockLocationChanged,
      sealNumberChanged,
      shipmentsWereChanged
    ].some((change) => change);
  };

  const onCancelEdits = () => {
    setLoadState(props.loadState);
    setSelectedShipments(selectedShipments.filter((shipment) => props.loadState?.shipmentIds?.includes(shipment.id)));
  };

  // returns T/F based on success
  const onUpsertLoad = async () => {
    setError(null);
    if (!editsWereMade()) {
      return;
    }
    try {
      const dockLocation = dockLocs.find((loc) => loc.location_label === loadState?.dockLocationLabel);
      const shipmentDestinationTags: Set<string> = new Set(
        selectedShipments.map((shipment) => shipment.destination_tag)
      );
      const destinationTag = Array.from(shipmentDestinationTags)[0];

      const data: UpsertLoadRequest = {
        loadId: props.loadState?.loadId,
        warehouseId: props.warehouseId,
        scac: loadState?.scac,
        trailerNumber: loadState?.trailerNumber,
        proNumber: loadState?.proNumber,
        sealNumber: loadState?.sealNumber,
        carrier: loadState?.carrier,
        shipmentIds: selectedShipments.map((shipment) => shipment.id),
        dockLocationId: dockLocation ? dockLocation.location_id : null,
        destinationTag
      };

      const response = await loadsService.upsertLoad(data);

      if (response.errors?.length <= 0) {
        if (upsertMode === UpsertMode.CREATE) {
          setCreationResultInfo({
            loadId: response.data.id
          });
          props.onCreateLoad();
        } else if (upsertMode === UpsertMode.EDIT) {
          const newLoadState = {
            ...loadState,
            destinationTag
          };
          setLoadState(newLoadState);
          props.onEditLoad(newLoadState); // this callback handles hiding the modal
          setShowPreview(false); // ensure modal renders page 1 on re-entry
        }
      } else {
        if (upsertMode === UpsertMode.CREATE) {
          setCreationResultInfo({
            error: response.errors[0].detail || 'An Internal Error Occurred',
            loadId: -1
          });
        } else if (upsertMode === UpsertMode.EDIT) {
          setError(response.errors[0].detail || 'An Internal Error Occurred');
          onCancelEdits();
        }
      }
    } catch (e) {
      setError(e.toString());
      onCancelEdits();
    }
  };

  const clearState = () => {
    setIsLoading(true);
    setError(null);
    setAvailableShipments([]);
    setSelectedShipments([]);
    setShipmentIdToDelete(null);
    setCreationResultInfo(null);
    setShowPreview(false);
    setRowSelectionModel([]);
    setLoadState(props.loadState);
  };

  return (
    <Modal isOpen={props.isOpen} toggleModal={handleToggle} title={upsertMode} size="full">
      {error != null && (
        <div className="alert alert-danger" role="alert" key={`error:${1}`}>
          {error}
        </div>
      )}
      <div id="upsert-load-modal-contents">
        {/*{!showPreview && (*/}
        {/*  <SelectShipments*/}
        {/*    selectedShipmentIds={rowSelectionModel}*/}
        {/*    data={availableShipments}*/}
        {/*    isLoading={isLoading}*/}
        {/*    rowSelectionModel={rowSelectionModel}*/}
        {/*    proceedToPreview={handleProceedToPreview}*/}
        {/*    handleSelectionModelChange={handleSelectionModelChange}*/}
        {/*  />*/}
        {/*)} // WMS-6029 */}
        {
          <PreviewShipments
            loadState={loadState}
            upsertMode={upsertMode}
            goBack={handleGoBack}
            onUpsertLoad={onUpsertLoad}
            selectedShipments={selectedShipments}
            handleDelete={handleDeleteId}
            creationResultInfo={creationResultInfo}
            toggleModal={handleToggle}
            vacantDockLocations={dockLocs}
            onEdit={(newLoadState) => setLoadState(newLoadState)}
          />
        }
      </div>
    </Modal>
  );
}
